// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import type {
  Action,
  BooleanRule,
  IndexedRule,
  ReasonCodeGroupMap,
  Rules,
  ServiceAttributeMap,
  Threshold,
  Attribute,
  ReasonCodeGroup,
  ServicesByName,
} from '../../types';
import { extractThresholdIds } from '~/dashboard/views/workflows/service/thresholds/thresholds';
import { booleanRuleStrings } from '../../constants';
import { getAttributeName } from './utils';
import { remapThresholdPublicIdRefsIntoNames } from './mapping';

/**
 * check if the threshold is new when compared with all of existing thresholds
 * by remapping thresholds data for actual config data comparison
 *
 * @param allThresholdsFromExistingRule all thresholds from the existing rule
 * @param compareTargetThreshold the new incoming threshold
 * @param serviceAttributeData provided from the pasted data
 * @param reasonCodeGroupData provided from the pasted data
 */
function checkIfMatchedThresholdsExist({
  allThresholdsFromExistingRule,
  compareTargetThreshold,
  actions,
  services,
  reasonCodeGroups,
  serviceAttributeData,
  reasonCodeGroupData,
}: {
  allThresholdsFromExistingRule: Threshold[];
  compareTargetThreshold: Threshold;
  actions: Record<string, Action>;
  services: ServicesByName;
  reasonCodeGroups: ReasonCodeGroup[];
  serviceAttributeData: ServiceAttributeMap;
  reasonCodeGroupData: ReasonCodeGroupMap;
}) {
  // iterate through all thresholds from the existing rule and return anything that's identical
  // to the incoming new threshold
  const mapOfAllEqualityComparison = allThresholdsFromExistingRule.filter(incomingThreshold => {
    // remap all public id based references to names of the object the public id was pointing to
    const remappedIncomingThreshold = remapThresholdPublicIdRefsIntoNames({
      actions,
      threshold: cloneDeep(incomingThreshold),
    });
    const remappedExistingThreshold = remapThresholdPublicIdRefsIntoNames({
      actions,
      threshold: cloneDeep(compareTargetThreshold),
    });

    let incomingThresholdRCGName;
    let existingThresholdRCGName;
    // if reason code group was used in both thresholds
    if (remappedIncomingThreshold.right.reasonCodeGroupId
      && remappedExistingThreshold.right.reasonCodeGroupId) {
      // get reason code group names
      // we compare like this because we simply rely on the name of RCGs for cloning/importing
      incomingThresholdRCGName = reasonCodeGroupData[
        remappedIncomingThreshold.right.reasonCodeGroupId]?.name
        || reasonCodeGroups.find(
          group => group.id === remappedExistingThreshold.right.reasonCodeGroupId)?.name;
      existingThresholdRCGName = reasonCodeGroups.find(
        group => group.id === remappedExistingThreshold.right.reasonCodeGroupId)?.name || '';

      if (incomingThresholdRCGName !== existingThresholdRCGName) {
        return false;
      }
    }

    let incomingThresholdLeftAttrName;
    let existingThresholdLeftAttrName;
    if (remappedIncomingThreshold.left.attributeId) {
      // get left attribute's name
      incomingThresholdLeftAttrName = getAttributeName({
        serviceAttributeData,
        services,
        attributeId: remappedIncomingThreshold.left.attributeId,
        sourceAction: actions[incomingThreshold.left.actionId],
      });
      remappedIncomingThreshold.left.attributeId = 0;
    }

    if (remappedExistingThreshold.left.attributeId) {
      // get left attribute's name
      existingThresholdLeftAttrName = getAttributeName({
        serviceAttributeData,
        services,
        attributeId: remappedExistingThreshold.left.attributeId,
        sourceAction: actions[compareTargetThreshold.left.actionId],
      });
      remappedExistingThreshold.left.attributeId = 0;
    }

    let incomingThresholdRightAttrName;
    let existingThresholdRightAttrName;
    if (remappedIncomingThreshold.right.attributeId
      && remappedExistingThreshold.right.attributeId) {
      // get right attribute's name if right action/right attribute was configured
      incomingThresholdRightAttrName = getAttributeName({
        serviceAttributeData,
        services,
        attributeId: remappedIncomingThreshold.right.attributeId,
        sourceAction: actions[incomingThreshold.right.actionId],
      });

      existingThresholdRightAttrName = getAttributeName({
        serviceAttributeData,
        services,
        attributeId: remappedExistingThreshold.right.attributeId,
        sourceAction: actions[compareTargetThreshold.right.actionId],
      });
      remappedIncomingThreshold.left.attributeId = 0;
      remappedExistingThreshold.left.attributeId = 0;
    }

    if ((incomingThresholdLeftAttrName !== existingThresholdLeftAttrName)
    || (incomingThresholdRightAttrName !== existingThresholdRightAttrName)) {
      return false;
    }

    if (isEqual(remappedIncomingThreshold, remappedExistingThreshold)) {
      return true;
    }

    return false;
  });

  // if there's anything returned as identical in this, we return true
  // as in matched thresholds exist
  if (mapOfAllEqualityComparison.length) {
    return true;
  }

  return false;
}

/**
 * check if the threshold is new when compared with all of existing thresholds
 *
 * @param rule the existing rule
 * @param compareTargetThreshold the new incoming threshold
 * @param serviceAttributeData provided from the pasted data
 * @param reasonCodeGroupData provided from the pasted data
 */
function getThresholdsWithDifferences({
  rule,
  compareTargetThreshold,
  thresholds,
  actions,
  services,
  reasonCodeGroups,
  serviceAttributeData,
  reasonCodeGroupData,
}: {
  rule: Rules;
  compareTargetThreshold: Threshold;
  thresholds: Record<string, Threshold>;
  actions: Record<string, Action>;
  services: ServicesByName;
  reasonCodeGroups: ReasonCodeGroup[];
  serviceAttributeData: ServiceAttributeMap;
  reasonCodeGroupData: ReasonCodeGroupMap;
}) {
  const thresholdsWithDifferences = [];

  // rule is referring to an existing rule
  // we extract all threshold ids from the existing rule in this workflow
  // and get the full threshold data of those ids
  const allThresholdsFromExistingRule = extractThresholdIds(rule).map(id => (
    thresholds[id]
  ));
  // get all thresholds that are matched (identical) with the new incoming threshold
  // which is compareTargetThreshold
  const thresholdMatches = checkIfMatchedThresholdsExist({
    allThresholdsFromExistingRule,
    compareTargetThreshold,
    actions,
    services,
    reasonCodeGroups,
    serviceAttributeData,
    reasonCodeGroupData,
  });

  // if no match, this threshold must be new!
  if (!thresholdMatches) {
    thresholdsWithDifferences.push({ id: compareTargetThreshold.publicId });
  }
  return thresholdsWithDifferences;
}

/**
 * recursively check if the rule/threshold id new by iterating through the new incoming rule
 * and call getThresholdsWithDifferences or itself recursively
 *
 * @param rule the new incoming rule
 * @param existingRule the existing rule
 * @param serviceAttributeData provided from the pasted data
 * @param reasonCodeGroupData provided from the pasted data
 */
function recursivelyCheckThreshold({
  rule,
  existingRule,
  thresholds,
  actions,
  services,
  reasonCodeGroups,
  serviceAttributeData,
  reasonCodeGroupData,
}: {
  rule: Rules[];
  existingRule: Rules;
  thresholds: Record<string, Threshold>;
  actions: Record<string, Action>;
  services: ServicesByName;
  reasonCodeGroups: ReasonCodeGroup[];
  serviceAttributeData: Record<number, {
    service: string;
    attribute: Attribute;
  }>;
  reasonCodeGroupData: Record<number, ReasonCodeGroup>;
}) {
  let allThresholdIdsWithDifferences = [];

  // rule item was and/or: [...] BooleanRule type, we have to iterate through rule items
  // so the rule passed here is the array
  // in the array and get all threshold ids referenced that are new to the existing workflow data
  rule.forEach(ruleValue => {
    if ('id' in (ruleValue as IndexedRule)) {
      // rule item is IndexedRule, we simply go check if the threshold id referenced
      // in this rule is new or not. if it's new, it'll be returned
      const thresholdsWithDifferences = getThresholdsWithDifferences({
        rule: existingRule,
        compareTargetThreshold: thresholds[(ruleValue as IndexedRule).id],
        thresholds,
        actions,
        services,
        reasonCodeGroups,
        serviceAttributeData,
        reasonCodeGroupData,
      });

      if (thresholdsWithDifferences.length) {
        allThresholdIdsWithDifferences = [
          ...allThresholdIdsWithDifferences,
          ...thresholdsWithDifferences,
        ];
      }
    } else {
      // rule item was and/or: [...] BooleanRule type, we have to iterate through rule items
      // so we recursively call this again
      const nestedRuleValue = ruleValue as BooleanRule;
      const thresholdsWithDifferences = recursivelyCheckThreshold({
        rule: nestedRuleValue.and || nestedRuleValue.or,
        existingRule,
        thresholds,
        actions,
        services,
        reasonCodeGroups,
        serviceAttributeData,
        reasonCodeGroupData,
      });

      const newKey = nestedRuleValue.and
        ? booleanRuleStrings.AND_OPERATOR_STRING
        : booleanRuleStrings.OR_OPERATOR_STRING;
      if (thresholdsWithDifferences.length > 1) {
        // we retain the rule item's and/or: [...] structure to respect the logical group established in this scope
        // because there are more than 1 threshold id referenced in this array that are new.
        allThresholdIdsWithDifferences.push({
          [newKey]: thresholdsWithDifferences,
        });
      }

      if (thresholdsWithDifferences.length === 1) {
        // in this case, there was only 1 threshold id referenced in this array that is new
        // so we should not retain the rule item's and/or: [...] structure
        allThresholdIdsWithDifferences.push(thresholdsWithDifferences[0]);
      }
    }
  });

  return allThresholdIdsWithDifferences;
}

/**
 * get any thresholds/boolean rule groupset (and/or: [ ... ]) that is new
 * when processing the pasted workflow data (this is used for partial paste user flow)
 *
 * @param incomingRule the rule coming from the pasted data
 * @param existingRule the rule in the existing action's rules object
 * @param serviceAttributeData provided from the pasted data
 * @param reasonCodeGroupData provided from the pasted data
 */
export function getDeepMergeableRules({
  incomingRule,
  existingRule,
  actions,
  thresholds,
  services,
  reasonCodeGroups,
  serviceAttributeData,
  reasonCodeGroupData,
}: {
  incomingRule: Rules;
  existingRule: Rules;
  actions: Record<string, Action>;
  thresholds: Record<string, Threshold>;
  services: ServicesByName;
  reasonCodeGroups: ReasonCodeGroup[];
  serviceAttributeData: ServiceAttributeMap;
  reasonCodeGroupData: ReasonCodeGroupMap;
}) {
  let thresholdIdsWithDifferences = [];
  // iterate through pasted rules
  Object.entries(incomingRule).forEach(([ruleKey, ruleValue]) => {
    if (ruleKey === 'id') {
      // rule item is IndexedRule, we simply go check if the threshold id referenced
      // in this rule is new or not. if it's new, it'll be returned
      const thresholdsWithDifferences = getThresholdsWithDifferences({
        rule: existingRule,
        compareTargetThreshold: thresholds[ruleValue],
        thresholds,
        actions,
        services,
        reasonCodeGroups,
        serviceAttributeData,
        reasonCodeGroupData,
      });

      thresholdIdsWithDifferences = [
        ...thresholdIdsWithDifferences,
        ...thresholdsWithDifferences,
      ];
    }

    if ([
      booleanRuleStrings.AND_OPERATOR_STRING,
      booleanRuleStrings.OR_OPERATOR_STRING,
    ].includes(ruleKey)) {
      // rule item is and/or: [...] BooleanRule type, we have to iterate through rule items
      // in the array and get all threshold ids referenced that are new to the existing workflow data
      const thresholdsWithDifferences = recursivelyCheckThreshold({
        rule: ruleValue,
        existingRule,
        thresholds,
        actions,
        services,
        reasonCodeGroups,
        serviceAttributeData,
        reasonCodeGroupData,
      });

      const newKey = ruleKey === booleanRuleStrings.AND_OPERATOR_STRING
        ? booleanRuleStrings.AND_OPERATOR_STRING
        : booleanRuleStrings.OR_OPERATOR_STRING;
      if (thresholdsWithDifferences.length > 1) {
        // we retain the rule item's and/or: [...] structure to respect the logical group established in this scope
        // because there are more than 1 threshold id referenced in this array that are new.
        thresholdIdsWithDifferences.push({
          [newKey]: thresholdsWithDifferences,
        });
      }

      if (thresholdsWithDifferences.length === 1) {
        // in this case, there was only 1 threshold id referenced in this array that is new
        // so we should not retain the rule item's and/or: [...] structure
        thresholdIdsWithDifferences.push(thresholdsWithDifferences[0]);
      }
    }
  });

  return thresholdIdsWithDifferences;
}
